import { useMutation } from "@apollo/client";
import { useDatabase } from "@nozbe/watermelondb/hooks";
import useOnline from "@towersystems/roam-common-native/lib/utilities/use-online";
import { StockCountItem } from "@towersystems/roam-common/lib/generated-types";
import { useEffect } from "react";
import { SyncState } from "../../../../utilities/data/constants";
import { useSyncableEntities } from "../../../../utilities/data/hooks";
import StockCountItemModel from "../../../../utilities/data/model/StockCountItem";
import { useQueue } from "../../../../utilities/queue-v2";
import { COUNT_STOCK_COUNT_ITEM } from "../../../../utilities/stock-count";
import { StockCountItemSyncContext } from "../../../../utilities/stock-count/context";

export interface StockCountSyncControllerProps {
  children?: React.ReactNode;
}

export function StockCountSyncController({
  children,
}: StockCountSyncControllerProps) {
  const { findSyncableEntities } = useSyncableEntities();
  const database = useDatabase();
  const canSync = useOnline();

  const [countStockCountItem] = useMutation<{
    countStockCountItem: StockCountItem;
  }>(COUNT_STOCK_COUNT_ITEM);

  const queue = useQueue({
    canSync,
    name: "counts",
    process,
  } as const);

  // perform remote adjustment on inventory movement item and move count to UPLOADED state
  async function process(stockCountItemId: string) {
    const time = new Date();
    const stockCountItem = await database.collections
      .get<StockCountItemModel>("stock_count_items")
      .find(stockCountItemId);

    if (!stockCountItem || stockCountItem.syncState !== SyncState.UPLOAD) {
      return false;
    }
    try {
      const localMovementItem = await stockCountItem.stockCountLine.fetch();
      const remoteResult = await countStockCountItem({
        variables: {
          stockCountId: localMovementItem.stockCount.id,
          input: {
            productId: localMovementItem.product.id,
            barcode: localMovementItem.barcode,
            counted: stockCountItem.counted,
            name: localMovementItem.name,
          },
        },
      });
      const remoteMovementItem = remoteResult.data?.countStockCountItem;
      if (!remoteMovementItem) throw new Error("No data returned from server");

      await database.write(async () => {
        await stockCountItem.update((c) => {
          c.syncState = SyncState.UPLOADED;
          c.lastSyncAttempt = time;
          c.numSyncAttempts += 1;
          c.remoteId = remoteMovementItem.id;
        });
      });

      queue.dequeue(stockCountItemId);
      return true;
    } catch (e) {
      await stockCountItem.increaseSyncAttempts(time);
    }

    return false;
  }

  async function addCountToQueue(stockCountItemId: string) {
    try {
      const syncableEntity = await database.collections
        .get<StockCountItemModel>("stock_count_items")
        .find(stockCountItemId);

      if (syncableEntity.syncState === SyncState.UPLOAD) {
        queue.enqueue(stockCountItemId);
      }

      return true;
    } catch (e) {}
    return false;
  }

  async function removeCountFromQueue(stockCountItemId: string) {
    try {
      const syncableEntity = await database.collections
        .get<StockCountItemModel>("stock_count_items")
        .find(stockCountItemId);

      if (syncableEntity.syncState === SyncState.UPLOAD) {
        queue.dequeue(stockCountItemId);
      }
    } catch (e) {}
  }

  async function processAndSyncCountById(stockCountItemId: string) {
    try {
      const success = await process(stockCountItemId);
      if (success) {
        await removeCountFromQueue(stockCountItemId);
      }
    } catch (e) {}
  }

  useEffect(() => {
    if (canSync) {
      findSyncableEntities<StockCountItemModel>("stock_count_items").then(
        (stockCountItems) => {
          queue.enqueue(stockCountItems.map((c) => c.id));
        }
      );
    }
  }, [canSync]);

  return (
    <StockCountItemSyncContext.Provider
      value={{
        running: queue.running,
        addCountToQueue,
        processAndSyncCountById,
        removeCountFromQueue,
      }}
    >
      {children}
    </StockCountItemSyncContext.Provider>
  );
}
